import React from "react";
import { Form, Typography } from "antd";
import CustomCard from "../../components/CustomCard";
import { AmountInput } from "../../components/form/AmountInput";
import type { OrderFinancialVO } from "../OrderForms/orders";
import { deriveZuuStep2Draft } from "./zuuStep2Draft";
import { isStrictPositiveInteger } from "./zuuValidation";
import styles from "../OrderForms/FinancialForm.module.css";

const { Title } = Typography;

const cx = (...classNames: Array<string | false | null | undefined>) => classNames.filter(Boolean).join(" ");

interface ZuuFinancialInfoStep2SectionProps {
  data: OrderFinancialVO;
  onChange: (data: OrderFinancialVO) => void;
  activeSection?: "financial-info" | "fna";
  isFinancialInfoRequired?: boolean;
  readOnly?: boolean;
  showRetirementFields?: boolean;
  detailDisplay?: boolean;
}

function parsePositiveInteger(value: number | undefined) {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return undefined;
  }
  const normalizedValue = Math.trunc(value);
  return normalizedValue >= 0 ? normalizedValue : undefined;
}

export function ZuuFinancialInfoStep2Section({
  data,
  onChange,
  activeSection = "financial-info",
  isFinancialInfoRequired,
  readOnly = false,
  showRetirementFields = false,
  detailDisplay = false,
}: ZuuFinancialInfoStep2SectionProps) {
  const zuuStep2Draft = deriveZuuStep2Draft({
    financial: data,
    activeSection,
    isFinancialInfoRequired,
  });

  const updateZuuDraftAmount = (
    field: "monthlyIncomeLast24Months" | "monthlyExpenseLast24Months" | "currentLiquidAssets" | "monthlyDisposableIncomeAfterRetirement" | "additionalLiquidAssetsAfterRetirement",
    value: number | undefined,
  ) => {
    const normalizedValue = parsePositiveInteger(value);
    const nextData: OrderFinancialVO = {
      ...data,
      monthlyIncomesTotal: undefined,
      monthlyExpensesTotal: undefined,
      liquidAssetsTotal: undefined,
      income: {
        ...(data.income || {}),
      },
      expense: {
        ...(data.expense || {}),
      },
      currentAssets: {
        ...(data.currentAssets || {}),
      },
    };

    if (field === "monthlyIncomeLast24Months") {
      nextData.income = {
        ...(nextData.income || {}),
        avgMonthlyIncome: normalizedValue ?? null,
      };
    } else if (field === "monthlyExpenseLast24Months") {
      nextData.expense = {
        ...(nextData.expense || {}),
        avgMonthlyExpense: normalizedValue ?? null,
      };
    } else if (field === "currentLiquidAssets") {
      nextData.currentAssets = {
        ...(nextData.currentAssets || {}),
        currentLiquidAssets: normalizedValue ?? null,
      };
    } else if (field === "monthlyDisposableIncomeAfterRetirement") {
      nextData.income = {
        ...(nextData.income || {}),
        expectedRetireIncome: normalizedValue ?? null,
      };
    } else if (field === "additionalLiquidAssetsAfterRetirement") {
      nextData.currentAssets = {
        ...(nextData.currentAssets || {}),
        expectedRetireLiquidAssets: normalizedValue ?? null,
      };
    }

    onChange(nextData);
  };

  const monthlyCurrencySuffix = "HKD/月";
  const currencySuffix = "HKD";
  const currencySuffixVariant = "box" as const;
  const headerBoxStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    backgroundColor: "#E9EEF5",
    border: "1px solid #103365",
    borderRadius: 8,
    padding: "12px 16px",
    fontWeight: 600,
    color: "#274C77",
    fontSize: 15,
    marginBottom: 16,
  };
  const subSectionHeaderStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
    color: "#475569",
  };
  const positiveIntegerRules = [
    {
      validator: async (_: unknown, value: unknown) => {
        if (!isFinancialInfoRequired || detailDisplay) {
          return;
        }
        if (isStrictPositiveInteger(value)) {
          return;
        }
        throw new Error("請輸入非負整數");
      },
    },
  ];

  return (
    <div className={cx(styles.financialForm, styles.zuuFinancialStepForm)}>
      <CustomCard title="一、財務資料" className={cx(styles.sectionCard, styles.zuuSectionCard)}>
        <div style={headerBoxStyle}>
          <span>1. 可動用收入</span>
          <span
            style={{
              fontSize: 11,
              color: "#5b6f87",
              fontWeight: 500,
            }}
          >
            ＊每月可動用收入將等同每月收入減去每月開支
          </span>
        </div>

        <div style={{ marginBottom: 24, borderRadius: 6 }}>
          <div style={subSectionHeaderStyle}>
            <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
              1a. 在過去二十四個月裡，您從所有收入來源所得的每月平均收入為？
            </Title>
          </div>
          <div className={styles.zuuQuestionNote}>包括薪金、花紅、佣金、其他薪酬福利、物業租賃收入、銀行存款利息、債券利息及股息等。</div>
          <div className={styles.zuuSummaryField}>
            <Form.Item name={["income", "avgMonthlyIncome"]} rules={positiveIntegerRules} validateTrigger={["onBlur", "onSubmit"]} style={{ marginBottom: 0 }}>
              <AmountInput
                style={{ width: "100%" }}
                suffix={monthlyCurrencySuffix}
                suffixVariant={currencySuffixVariant}
                value={zuuStep2Draft.financialInfo.availableIncome.monthlyIncomeLast24Months.amount}
                placeholder="請輸入"
                readOnly={readOnly || detailDisplay}
                disabled={readOnly && !detailDisplay}
                onChange={(val) => updateZuuDraftAmount("monthlyIncomeLast24Months", val)}
              />
            </Form.Item>
          </div>

          <div style={{ marginTop: 24 }}>
            <div style={subSectionHeaderStyle}>
              <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                1b. 在過去二十四個月裡，您每月平均開支為？
              </Title>
            </div>
            <div className={styles.zuuQuestionNote}>包括樓宇按揭、租金、衣服、交通、借貸、保險費用及保費融資產生的利息等。</div>
            <div className={styles.zuuSummaryField}>
              <Form.Item name={["expense", "avgMonthlyExpense"]} rules={positiveIntegerRules} validateTrigger={["onBlur", "onSubmit"]} style={{ marginBottom: 0 }}>
                <AmountInput
                  style={{ width: "100%" }}
                  suffix={monthlyCurrencySuffix}
                  suffixVariant={currencySuffixVariant}
                  value={zuuStep2Draft.financialInfo.availableIncome.monthlyExpenseLast24Months.amount}
                  placeholder="請輸入"
                  readOnly={readOnly || detailDisplay}
                  disabled={readOnly && !detailDisplay}
                  onChange={(val) => updateZuuDraftAmount("monthlyExpenseLast24Months", val)}
                />
              </Form.Item>
            </div>
          </div>

          {showRetirementFields && (
            <div style={{ marginTop: 20 }}>
              <div style={subSectionHeaderStyle}>
                <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                  1c. 於到達退休年齡後，您預期的平均每月可動用收入為？
                </Title>
              </div>
              <div className={styles.zuuQuestionNote}>
                註：一般而言，退休後的可動用收入可經由不同方式獲得，例如：銀行存款及債券所產生之利息、股票股息、租金收入、從年金產品所得的收入；及/或從家庭成員或信託所得的收入等等。
              </div>
              <div className={styles.zuuQuestionWarning}>*不適用於退休人士</div>
              <div className={styles.zuuPendingField}>
                <Form.Item name={["income", "expectedRetireIncome"]} rules={positiveIntegerRules} validateTrigger={["onBlur", "onSubmit"]} style={{ marginBottom: 0 }}>
                  <AmountInput
                    style={{ width: "100%" }}
                    suffix={monthlyCurrencySuffix}
                    suffixVariant={currencySuffixVariant}
                    placeholder="請輸入"
                    value={zuuStep2Draft.financialInfo.availableIncome.monthlyDisposableIncomeAfterRetirement.amount ?? undefined}
                    readOnly={readOnly || detailDisplay}
                    disabled={readOnly && !detailDisplay}
                    onChange={(val) => updateZuuDraftAmount("monthlyDisposableIncomeAfterRetirement", val)}
                  />
                </Form.Item>
              </div>
            </div>
          )}
        </div>

        <div style={headerBoxStyle}>
          <span>2. 流動資產</span>
          <span
            style={{
              fontSize: 11,
              color: "#5b6f87",
              fontWeight: 500,
            }}
          >
            ＊流動資產需按投資可隨時變現部分及退休後額外流動資產估算
          </span>
        </div>

        <div style={{ marginBottom: 24, borderRadius: 6 }}>
          <div style={subSectionHeaderStyle}>
            <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
              2a. 您現時累積的流動資產約有多少？
            </Title>
          </div>
          <div className={styles.zuuQuestionNote}>註：流動資產是指可以容易變現為現金的資產。物業、錢幣收藏及藝術品均不能被視為流動資產。</div>
          <div className={styles.zuuSummaryField}>
            <Form.Item name={["currentAssets", "currentLiquidAssets"]} rules={positiveIntegerRules} validateTrigger={["onBlur", "onSubmit"]} style={{ marginBottom: 0 }}>
              <AmountInput
                style={{ width: "100%" }}
                suffix={currencySuffix}
                suffixVariant={currencySuffixVariant}
                value={zuuStep2Draft.financialInfo.liquidAssets.currentLiquidAssets.amount}
                placeholder="請輸入"
                readOnly={readOnly || detailDisplay}
                disabled={readOnly && !detailDisplay}
                onChange={(val) => updateZuuDraftAmount("currentLiquidAssets", val)}
              />
            </Form.Item>
          </div>

          {showRetirementFields && (
            <div style={{ marginTop: 20 }}>
              <div style={subSectionHeaderStyle}>
                <Title level={5} style={{ margin: 0, color: "#333", fontSize: 14 }}>
                  2b. 除上述2a的金額，您預期到退休年齡後額外獲得的流動資產約有多少？
                </Title>
              </div>
              <div className={styles.zuuQuestionNote}>註：一般而言，退休後的流動資產可經由不同方式獲得，例如：強積金或退休金、銀行存款、股票或債券、遺產；及/或從保單的保證紅利/現金等等。</div>
              <div className={styles.zuuQuestionWarning}>*不適用於退休人士</div>
              <div className={styles.zuuPendingField}>
                <Form.Item name={["currentAssets", "expectedRetireLiquidAssets"]} rules={positiveIntegerRules} validateTrigger={["onBlur", "onSubmit"]} style={{ marginBottom: 0 }}>
                  <AmountInput
                    style={{ width: "100%" }}
                    suffix={currencySuffix}
                    suffixVariant={currencySuffixVariant}
                    placeholder="請輸入"
                    value={zuuStep2Draft.financialInfo.liquidAssets.additionalLiquidAssetsAfterRetirement.amount ?? undefined}
                    readOnly={readOnly || detailDisplay}
                    disabled={readOnly && !detailDisplay}
                    onChange={(val) => updateZuuDraftAmount("additionalLiquidAssetsAfterRetirement", val)}
                  />
                </Form.Item>
              </div>
            </div>
          )}
        </div>
      </CustomCard>
    </div>
  );
}
